import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { getTeam, leaveTeam } from '../../api/runs';
import { ApiError } from '../../api/client';
import type { Team } from '../../api/types';
import { useAuth } from '../../auth/useAuth';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';

interface TeamMemberDTO {
  id: string;
  display_name: string;
  is_guest: boolean;
}

interface TeamWithMembers extends Team {
  members?: TeamMemberDTO[];
}

const TeamInfo: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { user, logout } = useAuth();
  const { t } = useTranslation('runs');
  const navigate = useNavigate();

  const [team, setTeam] = useState<TeamWithMembers | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const handleLeave = async () => {
    if (!user?.organization_slug || !runId || !team) return;
    setLeaving(true);
    setLeaveError(null);
    try {
      await leaveTeam(user.organization_slug, runId, team.id);
      sessionStorage.removeItem(`clavis_team_for_run_${runId}`);
      if (user.role === 'guest') {
        // A departed guest cannot re-join this run (SOW 8.2.6 re-join block)
        // and has no other surface in the app — end the session cleanly.
        // Navigate to the public Welcome screen BEFORE clearing the session:
        // logout() re-renders the ProtectedRoute shell, whose /login redirect
        // would otherwise win the race against our navigate.
        navigate('/', { replace: true });
        await logout();
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setLeaveError(err instanceof ApiError ? err.detail : String(err));
      setLeaving(false);
    }
  };

  useEffect(() => {
    if (!user?.organization_slug || !runId) return;
    const teamId = sessionStorage.getItem(`clavis_team_for_run_${runId}`);
    if (!teamId) {
      setError('No team membership found for this run.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    getTeam(user.organization_slug, runId, teamId)
      .then((data) => { if (!cancelled) setTeam(data as TeamWithMembers); })
      .catch((err) => { if (!cancelled) setError(String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.organization_slug, runId]);

  if (loading) return <LoadingSpinner />;
  if (error || !team) return <ErrorAlert message={error ?? 'Team not found'} />;

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: team.color ?? 'primary.main',
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5">{team.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {team.member_count} {team.member_count === 1 ? 'Member' : 'Members'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* No share-code block here — the TeamTab layout above the sub-tabs
          already renders it; repeating it doubled the code on this view. */}
      {team.members && team.members.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" component="h2" gutterBottom>
              {t('team.info.membersTitle')}
            </Typography>
            <Divider />
            <List>
              {team.members.map((m) => (
                <ListItem key={m.id} disablePadding sx={{ py: 0.5 }}>
                  <ListItemAvatar>
                    <Avatar><PersonIcon /></Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={m.display_name} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      <Box>
        {leaveError && <ErrorAlert message={leaveError} />}
        <Button
          color="error"
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={() => setLeaveOpen(true)}
        >
          {t('team.leave.button')}
        </Button>
      </Box>

      <Dialog open={leaveOpen} onClose={() => !leaving && setLeaveOpen(false)}>
        <DialogTitle>{t('team.leave.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {user?.role === 'guest'
              ? t('team.leave.confirmMessageGuest')
              : t('team.leave.confirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveOpen(false)} disabled={leaving}>
            {t('team.leave.cancel')}
          </Button>
          <Button color="error" onClick={handleLeave} disabled={leaving}>
            {t('team.leave.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default TeamInfo;
